import { useNavigate } from "react-router-dom";
// INFO: after a successful sign-in the user is normally sent to "/". A flow that needs the
// user back on a specific in-app route (e.g. the MCP connector consent screen) stores that
// route here first; we consume it once and clear it. The old document.referrer approach was
// unreliable (empty on cross-origin, includes the /auth page itself), hence this explicit
// hand-off via sessionStorage.
var RETURN_URL_KEY = "rentio:auth:returnUrl";
// INFO: browsers drop tabs and newlines from a URL and read a backslash as a slash, so
// "/\evil.com" and "/<tab>evil.com" both turn into the protocol-relative "//evil.com". Checked on
// the raw value and on its decoded form, so "/%09evil.com" cannot smuggle one in either.
var hasUnsafePathCharacter = function (value) {
    return value.split("").some(function (character) {
        var code = character.charCodeAt(0);
        return (character === "\\" || code < 0x20 || code === 0x7f || /\s/.test(character));
    });
};
// INFO: only same-origin absolute paths are allowed, to avoid an open redirect. Must start
// with a single "/" (not "//", which is protocol-relative and points off-site), and must
// still resolve to this origin once the browser has parsed it.
export var isSafeInternalPath = function (value) {
    if (!(value === null || value === void 0 ? void 0 : value.startsWith("/")) || value.startsWith("//")) {
        return false;
    }
    var decoded;
    try {
        decoded = decodeURIComponent(value);
    }
    catch (_a) {
        // A stray "%" is not a path we ever store ourselves.
        return false;
    }
    if (hasUnsafePathCharacter(value) || hasUnsafePathCharacter(decoded)) {
        return false;
    }
    try {
        return (new URL(value, window.location.origin).origin === window.location.origin);
    }
    catch (_b) {
        return false;
    }
};
export var setReturnUrlAfterLogin = function (path) {
    try {
        if (isSafeInternalPath(path)) {
            sessionStorage.setItem(RETURN_URL_KEY, path);
        }
    }
    catch (_a) {
        // sessionStorage can be unavailable (private mode); the flow still works, just without
        // the return hand-off.
    }
};
var consumeReturnUrl = function () {
    try {
        var value = sessionStorage.getItem(RETURN_URL_KEY);
        if (value) {
            sessionStorage.removeItem(RETURN_URL_KEY);
        }
        return isSafeInternalPath(value) ? value : null;
    }
    catch (_a) {
        return null;
    }
};
// INFO: which sign-in page a session belongs on once it ends.
//
// An SSO session is refused at refresh on purpose — that refusal is what makes offboarding at
// the identity provider take effect without automatic de-provisioning, because the next
// sign-in has to go back through it. The token therefore expires roughly hourly and the route
// guard sees a signed-out user. Sending that user to /auth/signin is a dead end: their office's
// enforceSsoOnly setting makes the API refuse email and phone sign-in, so the page they land on
// is the one page that cannot let them back in.
//
// localStorage rather than sessionStorage: this has to outlive the tab, because the session it
// describes does. Cleared on a successful email sign-in and when someone deliberately asks for
// the password page, so a shared machine does not keep sending the next person to the previous
// person's office.
var SSO_SESSION_KEY = "rentio:auth:ssoSession";
export var rememberSsoSession = function () {
    try {
        localStorage.setItem(SSO_SESSION_KEY, "1");
    }
    catch (_a) {
        // Private mode and blocked site data both throw. The cost is a bounce to /auth/signin,
        // which is the behavior before this existed, so there is nothing to fall back to.
    }
};
export var forgetSsoSession = function () {
    try {
        localStorage.removeItem(SSO_SESSION_KEY);
    }
    catch (_a) {
        // As above.
    }
};
export var hasSsoSession = function () {
    try {
        return localStorage.getItem(SSO_SESSION_KEY) === "1";
    }
    catch (_a) {
        return false;
    }
};
export var useNavigateAfterSuccess = function () {
    var navigate = useNavigate();
    var navigateAfterSuccess = function () {
        var returnUrl = consumeReturnUrl();
        navigate(returnUrl !== null && returnUrl !== void 0 ? returnUrl : "/", { replace: true });
    };
    return navigateAfterSuccess;
};
